import { z } from 'zod';
import { GateId } from './activation.ts';

/**
 * Added by 21 step 0 (brief 21 §1, §2; ADR-021): what happens when a work item, an obligation or (additively, through
 * `ApprovalEscalation`) an approval stage is not done in time. The orchestrator (work items), the obligation watcher
 * (`platform.obligation-watch`) and the approvals module act on it **before** the waiting handler sees the timeout.
 *
 * - `reassign`: the item goes to `principalId` (or to holders of `role`); replay identity stays the activation id.
 * - `add_role`: holders of `role` may also claim (the original owner role keeps its right).
 * - `notify`: a notification is sent through the `Notifier` port with `templateKey`; nothing else changes.
 * - `raise_gate`: an approval gate is opened for the overdue subject (the gate's own route decides who decides).
 *
 * An escalation never grants approval authority: authority is only an approval-gate stage (§1b.1).
 */
export const EscalationMode = z
  .enum(['reassign', 'add_role', 'notify', 'raise_gate'])
  .meta({ id: 'EscalationMode' });
export type EscalationMode = z.infer<typeof EscalationMode>;

export const Escalation = z
  .object({
    /** Delay after the deadline (or after creation when there is no deadline) before the escalation runs; 0 = at once. */
    afterMs: z.number().int().nonnegative().optional(),
    mode: EscalationMode,
    /** `reassign` / `add_role`: the role whose holders may claim. */
    role: z.string().min(1).optional(),
    /** `reassign`: the principal the item goes to. */
    principalId: z.string().min(1).optional(),
    /** `notify` (and optionally the other modes): the template key the notification service renders. */
    templateKey: z.string().min(1).max(200).optional(),
    /** `raise_gate`: the gate to open (declared by the process). */
    gateId: GateId.optional(),
  })
  .superRefine((e, ctx) => {
    if (e.mode === 'reassign' && e.role === undefined && e.principalId === undefined) {
      ctx.addIssue({ code: 'custom', message: 'reassign needs role or principalId', path: ['role'] });
    }
    if (e.mode === 'add_role' && e.role === undefined) {
      ctx.addIssue({ code: 'custom', message: 'add_role needs role', path: ['role'] });
    }
    if (e.mode === 'raise_gate' && e.gateId === undefined) {
      ctx.addIssue({ code: 'custom', message: 'raise_gate needs gateId', path: ['gateId'] });
    }
    if (e.mode === 'notify' && e.templateKey === undefined) {
      ctx.addIssue({ code: 'custom', message: 'notify needs templateKey', path: ['templateKey'] });
    }
  })
  .meta({ id: 'Escalation' });
export type Escalation = z.infer<typeof Escalation>;
export type EscalationInput = z.input<typeof Escalation>;
